import { useEffect, useRef, useState } from 'react';

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: 'ri-layout-4-line',
      title: 'Custom Design',
      description: 'Modern and unique interface that reflects your identity',
      color: 'from-[#FFE5E5] to-[#FFF0F0]'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Responsive',
      description: 'Perfect on mobile, tablet and desktop',
      color: 'from-[#E5FFF0] to-[#F0FFF5]'
    },
    {
      icon: 'ri-speed-line',
      title: 'Performance',
      description: 'Fast websites optimized for SEO',
      color: 'from-[#FFF9E5] to-[#FFFCF0]'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'User Experience',
      description: 'Intuitive navigation that converts your visitors',
      color: 'from-[#F5F5FF] to-[#FAFAFF]'
    }
  ];

  return (
    <section ref={sectionRef} id="services" className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-[#FF6B6B]/5 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-[#0A1628]/5 rounded-full filter blur-3xl"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-20 relative z-10">
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 bg-[#FF6B6B]/10 text-[#FF6B6B] px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></span>
            Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-[#0A1628] mb-4 md:mb-6">
            What I create<br />
            <span className="text-[#FF6B6B] italic">for you</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[#0A1628]/60 max-w-2xl mx-auto px-4">
            Functional, clean and easy-to-navigate showcase websites, perfectly suited for small businesses
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`group bg-gradient-to-br ${service.color} rounded-2xl md:rounded-3xl p-6 md:p-8 hover:-translate-y-3 hover:shadow-2xl transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                <i className={`${service.icon} text-2xl md:text-3xl text-[#FF6B6B]`}></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#0A1628] mb-2 md:mb-3">
                {service.title}
              </h3>
              <p className="text-[#0A1628]/60 text-sm leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        {/* Features list */}
        <div className={`mt-12 md:mt-16 lg:mt-20 bg-white rounded-2xl md:rounded-[32px] shadow-xl p-6 md:p-10 lg:p-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {[
              { icon: 'ri-checkbox-circle-fill', text: 'Essential features without complexity' },
              { icon: 'ri-checkbox-circle-fill', text: 'Mobile optimized and fast loading' },
              { icon: 'ri-checkbox-circle-fill', text: 'Fast and efficient delivery' },
              { icon: 'ri-checkbox-circle-fill', text: 'Professional design that inspires trust' },
              { icon: 'ri-checkbox-circle-fill', text: 'Basic SEO configured' },
              { icon: 'ri-checkbox-circle-fill', text: 'Integrated contact form' }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 md:gap-4 group">
                <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-xl md:text-2xl text-[#FF6B6B] group-hover:scale-125 transition-transform`}></i>
                </div>
                <p className="text-sm md:text-base text-[#0A1628]/70">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
